/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
/* BEGIN HARCOURT ORDERING NOTICE */
(function () {
  const storageKey = 'harcourt.ordering-notice.dismissed-until.v1';
  const dismissalDuration = 30 * 24 * 60 * 60 * 1000;
  const recentInteractions = new WeakMap();
  let dismissedUntil = 0;
  try { dismissedUntil = Number(localStorage.getItem(storageKey)) || 0; } catch (_) {}
  function isDismissed() { return dismissedUntil > Date.now(); }
  function hideNotices() {
    document.querySelectorAll('.hc-ordering-notice').forEach((notice) => notice.remove());
  }
  function showNotice(info) {
    if (!info.isConnected || isDismissed() || info.querySelector('.hc-ordering-notice')) return;
    const form = info.querySelector('.product-form');
    if (!form) return;
    const notice = document.createElement('div');
    notice.className = 'hc-ordering-notice';
    notice.innerHTML = '<div role="status" aria-live="polite"><strong class="hc-ordering-notice__title">Ordering coming soon!</strong><p>Explore colors and sizes now. <a href="mailto:orders@harcourtcollection.com">Contact us</a> for product inquiries.</p></div><button type="button" class="hc-ordering-notice__close" aria-label="Dismiss ordering notice">&times;</button>';
    notice.querySelector('button').addEventListener('click', () => {
      const returnFocus = notice.contains(document.activeElement);
      dismissedUntil = Date.now() + dismissalDuration;
      try { localStorage.setItem(storageKey, String(dismissedUntil)); } catch (_) {}
      hideNotices();
      if (returnFocus) {
        const selectedOption = info.querySelector('input[data-option-position]:checked, .swatches [tabindex="0"], variant-swatch-king [tabindex="0"], .select');
        selectedOption?.focus({ preventScroll: true });
      }
    });
    // Prefer the visible app selector if it replaces the native variant picker.
    const selectors = Array.from(info.querySelectorAll('variant-swatch-king, .swatches, variant-picker'));
    const anchor = selectors.find((element) => element.getClientRects().length > 0);
    if (anchor) anchor.after(notice);
    else form.prepend(notice);
  }
  function scheduleNotice(info) {
    // Let the theme finish replacing selector markup after its variant event.
    requestAnimationFrame(() => showNotice(info));
  }
  function onInteraction(event) {
    const target = event.target;
    if (!(target instanceof Element) || !event.isTrusted) return;
    const info = target.closest('.product__info');
    if (!info || target.closest('.hc-ordering-notice')) return;
    recentInteractions.set(info, Date.now());
    const option = target.closest('[data-option-position], .block-swatch__item, .color-swatch__item, .variant-swatch__item, .combo-box__option-item, .swatch-view li, variant-swatch-king label, variant-swatch-king [role="radio"]');
    if (option && info.contains(option) && (event.type === 'click' || event.type === 'change')) scheduleNotice(info);
  }
  // Delegation covers selectors added later by apps or section rendering.
  document.addEventListener('click', onInteraction, true);
  document.addEventListener('change', onInteraction, true);
  document.addEventListener('keydown', onInteraction, true);
  document.addEventListener('variant:changed', (event) => {
    const info = event.target instanceof Element ? event.target.closest('.product__info') : null;
    const interactedAt = info && recentInteractions.get(info);
    // Ignore app initialization without a recent shopper interaction.
    if (interactedAt !== undefined && interactedAt !== null && Date.now() - interactedAt < 10000) scheduleNotice(info);
  });
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    dismissedUntil = Number(event.newValue) || 0;
    if (isDismissed()) hideNotices();
  });
})();
/* END HARCOURT ORDERING NOTICE */
